import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, ZONE_DETAILS, formatAED } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import useSEO from "../lib/useSEO";
import { useT } from "../lib/i18n";
import { breadcrumbLd, zonePlaceLd, itemListLd } from "../lib/jsonld";

export default function ZoneDetail() {
  const { t } = useT();
  const { slug } = useParams();
  const navigate = useNavigate();
  const zoneEntry = Object.entries(ZONE_DETAILS).find(([, z]) => z.slug === slug);
  const [zoneName, zone] = zoneEntry || [null, null];

  const [properties, setProperties] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!zone) {
      navigate("/zones");
      return;
    }
    api.get(`/properties?zone=${encodeURIComponent(zoneName)}&limit=24`).then((r) => {
      setProperties(r.data);
      setLoading(false);
    });
  }, [zone, zoneName, navigate]);

  const jsonLd = React.useMemo(() => {
    if (!zone || !zoneName) return [];
    return [
      breadcrumbLd([
        { name: "Home", url: "/" },
        { name: "Zones", url: "/zones" },
        { name: zoneName, url: `/zones/${slug}` },
      ]),
      zonePlaceLd(zoneName, zone, properties.length),
      properties.length ? itemListLd(properties) : null,
    ].filter(Boolean);
  }, [zone, zoneName, slug, properties]);

  useSEO({
    title: zone ? `${zoneName} Warehouses & Industrial Properties for Rent — WarehousesUAE Dubai` : "Zone — WarehousesUAE",
    description: zone?.seo || "",
    image: zone?.img,
    canonical: window.location.origin + `/zones/${slug}`,
    jsonLd,
  });

  if (!zone) return null;

  const avgPrice = properties.length
    ? Math.round(properties.reduce((s, p) => s + (p.price_per_sqft || 0), 0) / properties.length)
    : 0;
  const totalSize = properties.reduce((s, p) => s + (p.size_sqft || 0), 0);

  return (
    <div data-testid={`zone-page-${slug}`} className="bg-zinc-50 min-h-screen">
      {/* Hero */}
      <section className="relative bg-zinc-950 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${zone.img})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent"></div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
          <Link to="/zones" className="font-mono-tech text-[10px] uppercase text-zinc-400 hover:text-white mb-3 inline-block">{t("zone_detail.back")}</Link>
          <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] mb-3">{t("zone_detail.kicker")}</div>
          <h1 className="font-display text-6xl md:text-8xl tracking-tighter leading-[0.9] mb-4">{zoneName}</h1>
          <p className="text-zinc-300 text-lg max-w-2xl">{zone.description}</p>

          <div className="mt-8 flex flex-wrap gap-2">
            {zone.tags.map((tag) => (
              <span key={tag} className="bg-white/10 border border-white/20 px-3 py-1.5 font-mono-tech text-[11px] uppercase">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid grid-cols-3 gap-px bg-zinc-200">
          {[
            { l: t("zone_detail.stat_listings"), v: properties.length },
            { l: t("zone_detail.stat_sqft"), v: formatAED(totalSize) },
            { l: t("zone_detail.stat_avg"), v: avgPrice || "—" },
          ].map((s, i) => (
            <div key={i} className="bg-white p-6">
              <div className="font-display text-3xl md:text-4xl tracking-tighter text-[#031833]">{s.v}</div>
              <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Listings */}
      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">// {zoneName.toUpperCase()}</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-8">
            {t("zone_detail.available_title")} {zoneName}
          </h2>
          {loading ? (
            <div className="font-mono-tech text-sm text-zinc-500">Loading inventory...</div>
          ) : properties.length === 0 ? (
            <div className="border border-dashed border-zinc-300 p-12 text-center bg-white">
              <div className="font-display text-2xl tracking-tighter mb-2">{t("zone_detail.no_public")}</div>
              <p className="text-zinc-600 mb-4">{t("zone_detail.no_public_sub")}</p>
              <Link to="/contact" className="inline-block bg-zinc-950 text-white font-mono-tech text-xs uppercase px-5 py-3">{t("zone_detail.request")}</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
